import type { InspectionTemplateModel } from 'shared/domain/template/inspectionTemplateModel';
import {
  ChartAxisSeries,
  ChartValuesAndLabels,
  FilterValues,
  InspectionChart,
  IssueChart,
  TripletSeriesWithLabels,
} from 'shared/types/analytics';

export type AnalyticsChart = IssueChart | InspectionChart;

/** The series each chart's calculation returns. */
export interface ChartSeriesByChart {
  [IssueChart.originator]: ChartValuesAndLabels;
  [IssueChart.rootCause]: ChartValuesAndLabels;
  [IssueChart.impact]: ChartValuesAndLabels;
  [IssueChart.workType]: ChartValuesAndLabels;
  [IssueChart.environmentalAspect]: ChartValuesAndLabels;
  [IssueChart.hsEffect]: ChartValuesAndLabels;
  [IssueChart.hsHazardType]: ChartValuesAndLabels;
  [InspectionChart.completedInspectionsByTemplate]: ChartValuesAndLabels;
  [InspectionChart.inspectionEvolution]: ChartAxisSeries;
  [InspectionChart.inspectionTriplet]: TripletSeriesWithLabels;
}

export type ChartSeries<TChart extends AnalyticsChart = AnalyticsChart> = ChartSeriesByChart[TChart];

/** Data a chart's calculation needs besides the filters. */
export interface ChartAdditionalParams {
  /** Inspection templates of the project (completed inspections by template). */
  templates?: InspectionTemplateModel[];
  /** Processes the user may see (issue charts by field value). */
  visibleProcesses?: string[];
}

export interface ChartRequest<TChart extends AnalyticsChart = AnalyticsChart> {
  chart: TChart;
  filters: FilterValues;
  locale: string;
  additionalParams?: ChartAdditionalParams;
  /** Calculates again even when the cached chart still matches. */
  forceUpdate?: boolean;
}

export interface ChartResult<TChart extends AnalyticsChart = AnalyticsChart> {
  chart: TChart;
  series: ChartSeries<TChart>;
  filters: FilterValues;
  locale: string;
  additionalParams?: ChartAdditionalParams;
  /** When the series was calculated (ms). */
  updatedAt: number;
}

/** Params the caller is still resolving (e.g. templates not loaded yet): the chart waits for them. */
export function hasUnresolvedChartParams(additionalParams: ChartAdditionalParams | undefined): boolean {
  return additionalParams !== undefined && Object.values(additionalParams).some((value) => value === undefined);
}
